import { intlLocale, tAny } from '@/i18n'

// Formatters are cached per locale and rebuilt when the interface language changes.
const cache = new Map<
  string,
  {
    date: Intl.DateTimeFormat
    dateTime: Intl.DateTimeFormat
    number: Intl.NumberFormat
    month: Intl.DateTimeFormat
  }
>()

function fmt() {
  const locale = intlLocale()
  let f = cache.get(locale)
  if (!f) {
    f = {
      date: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }),
      dateTime: new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      number: new Intl.NumberFormat(locale),
      month: new Intl.DateTimeFormat(locale, { month: 'short' }),
    }
    cache.set(locale, f)
  }
  return f
}

function parse(value: string): Date {
  return new Date(value.length === 10 ? `${value}T00:00:00` : value)
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = parse(value)
  return Number.isNaN(date.getTime()) ? '—' : fmt().date.format(date)
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const date = parse(value)
  return Number.isNaN(date.getTime()) ? '—' : fmt().dateTime.format(date)
}

/** "2026-03" → "Mar" / "mars" in the active language. */
export function formatMonthShort(yearMonth: string): string {
  const date = parse(`${yearMonth}-01`)
  return Number.isNaN(date.getTime()) ? yearMonth : fmt().month.format(date)
}

export function formatNumber(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : fmt().number.format(value)
}

// Currencies written after the amount whatever the language ("150 000 Ar", never "Ar 150,000").
const SYMBOL_AFTER = new Set(['MGA'])

/** Amount in the school's currency, with the local symbol ("Ar", "€", "$"). */
export function formatMoney(value: string | number | null | undefined, currency = 'USD'): string {
  if (value === null || value === undefined || value === '') return '—'
  return formatCurrency(Number(value), currency, { maximumFractionDigits: 2 })
}

/** Intl currency formatting with the narrow symbol, moved after the amount for SYMBOL_AFTER currencies. */
export function formatCurrency(
  value: number,
  currency: string,
  options: Intl.NumberFormatOptions = {},
): string {
  const parts = new Intl.NumberFormat(intlLocale(), {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    ...options,
  }).formatToParts(value)
  if (!SYMBOL_AFTER.has(currency.toUpperCase())) return parts.map((p) => p.value).join('')
  const symbol = parts.find((p) => p.type === 'currency')?.value ?? currency
  const amount = parts
    .filter((p) => p.type !== 'currency')
    .map((p) => p.value)
    .join('')
    .trim()
  return `${amount}\u00a0${symbol}`
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/)
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')).toUpperCase()
}

/**
 * Human label for an enum value: "local_guardian" → "Local guardian", then translated (the
 * English label is the translation key, so enum labels are translated in one place).
 */
export function humanize(value: string | null | undefined): string {
  if (!value) return '—'
  const text = value.replace(/[_-]+/g, ' ').trim()
  return tAny(text.charAt(0).toUpperCase() + text.slice(1))
}

export function ageFrom(dateOfBirth: string | null | undefined): number | null {
  if (!dateOfBirth) return null
  const dob = new Date(`${dateOfBirth}T00:00:00`)
  const now = new Date()
  let age = now.getFullYear() - dob.getFullYear()
  const m = now.getMonth() - dob.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--
  return age
}

/** 37.8 → "37.8%" (en) / "37,8 %" (fr). Takes a value already expressed in percent. */
export function formatPercent(value: number | string | null | undefined, maxFractionDigits = 1): string {
  if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) return '—'
  return new Intl.NumberFormat(intlLocale(), {
    style: 'percent',
    maximumFractionDigits: maxFractionDigits,
  }).format(Number(value) / 100)
}

/** "512 B" / "34 KB" / "1.5 MB" in the UI language. */
export function formatBytes(size: number): string {
  const number = (value: number, digits: number) =>
    new Intl.NumberFormat(intlLocale(), {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      useGrouping: false,
    }).format(value)
  if (size < 1024) return tAny('{{size}} B', { size })
  if (size < 1024 * 1024) return tAny('{{size}} KB', { size: number(size / 1024, 0) })
  return tAny('{{size}} MB', { size: number(size / 1024 / 1024, 1) })
}
